import { yupResolver } from '@hookform/resolvers/yup';
import { useSnackbar } from 'notistack';
import { useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { useLocation, useNavigate, useParams } from 'react-router';
import { paths } from 'src/routes/paths';
import { useScnEventsStore } from 'src/store/benefits/scn-events.store';
import * as Yup from 'yup';

export default function useScnEventsDetails() {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const { pathname } = useLocation();
  const {
    loadingScnEventCreate,
    scnEventDetails,
    loadingScnEventDetails,
    loadingScnEventUpdate,
    createScnEvent,
    updateScnEvent,
    getScnEventDetails,
    resetScnEventDetails,
  } = useScnEventsStore();
  const isEdit = pathname.includes('edit');
  const { id } = useParams();
  const currentDate = new Date();

  const STATUS_OPTIONS = ['active', 'inactive'];
  const STATUS_LABEL = ['Ativo', 'Inativo'];

  useEffect(() => {
    const fetchDetails = async () => {
      if (id) {
        try {
          await getScnEventDetails(id);
        } catch (error) {
          enqueueSnackbar(error.message, { variant: 'error' });
          navigate(paths.dashboard.scnEventsList);
        }
      } else {
        resetScnEventDetails();
      }
    };

    fetchDetails();
  }, []);

  const defaultValues = useMemo(
    () => ({
      name: scnEventDetails?.name || '',
      startDate: scnEventDetails?.startDate ? new Date(scnEventDetails.startDate) : new Date(currentDate.getFullYear(), currentDate.getMonth(), currentDate.getDate()),
      endDate: scnEventDetails?.endDate ? new Date(scnEventDetails.endDate) : new Date(currentDate.getFullYear(), currentDate.getMonth(), currentDate.getDate()),
      status: scnEventDetails?.status || 'active',
    }),
    [scnEventDetails]
  );

  const scnEventSchema = Yup.object().shape({
    name: Yup.string().required('Campo obrigatório!'),
    startDate: Yup.date().required('Data de início obrigatória!'),
    endDate: Yup.date()
      .required('Data de fim obrigatória!')
      .min(Yup.ref('startDate'), 'Data de fim deve ser após a data de início!'),
    status: Yup.string().required('Campo obrigatório!'),
  });

  const methods = useForm({
    resolver: yupResolver(scnEventSchema),
    defaultValues,
  });

  const {
    handleSubmit,
    reset,
    control,
    formState: { isSubmitting },
  } = methods;

  useEffect(() => {
    if (defaultValues) {
      reset(defaultValues);
    }
  }, [scnEventDetails]);

  const onSubmit = handleSubmit(async (data: any) => {
    try {
      const updatedData = {
        name: data.name,
        startDate: data.startDate,
        endDate: data.endDate,
        status: data.status,
      };

      if (isEdit && id) {
        await updateScnEvent(id, updatedData);
        enqueueSnackbar('Evento atualizado com sucesso!', { variant: 'success' });
      } else {
        await createScnEvent(updatedData);
        enqueueSnackbar('Evento criado com sucesso!', { variant: 'success' });
      }
      navigate(paths.dashboard.scnEventsList);
    } catch (error) {
      if (error?.message) {
        enqueueSnackbar(error.message, { variant: 'error' });
      } else {
        enqueueSnackbar(isEdit ? 'Erro ao editar evento SCN!' : 'Erro ao criar evento SCN!', {
          variant: 'error',
        });
      }
    }
  });

  return {
    methods,
    isSubmitting,
    loadingScnEventCreate,
    loadingScnEventDetails,
    loadingScnEventUpdate,
    scnEventDetails,
    isEdit,
    control,
    STATUS_OPTIONS,
    STATUS_LABEL,
    navigate,
    reset,
    onSubmit,
  };
}
